import { secondsToHours, parseInternalDatetime } from '~/utils/time_utils';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';

export default {
  computed: {
    isTimeBasedMetric() {
      const timeBasedMetrics = [
        'work_time', 'time_spent_in_tool', 'unusual_activity', 'suspicious_activity', 'idle_time', 'manual_time'
      ];

      return timeBasedMetrics.includes(this.rule.metric_type);
    },
    isCountMetric() {
      return SETTINGS_SMART_NOTIFICATIONS.options.countMetrics.includes(this.rule.metric_type);
    },
    countMetricAddon() {
      switch (this.rule.metric_type) {
        case 'users_per_ip':
          return 'users';
        case 'geo_ip_list_match':
          return 'matches';
        default:
          return 'changes';
      }
    },
    isGeoIpListMatch() {
      return this.rule.metric_type === 'geo_ip_list_match';
    },
    geoIpScopeLabel() {
      const scope = this.rule.data?.scope;

      return { ip: 'IPs', city: 'cities', country: 'countries' }[scope] || scope;
    },
    // Human-readable summary of a geo-IP list-match rule, e.g.
    // "Geo-IP is not in any of the following cities". The actual list is rendered separately.
    geoIpListIntro() {
      const preposition = this.rule.data?.match_mode === 'not_in'
        ? 'not in any of the following'
        : 'in 1 or more of the following';

      return `Geo-IP is ${preposition} ${this.geoIpScopeLabel}`;
    },
    geoIpList() {
      const list = this.rule.data?.list || [];

      // For the `country` scope the stored list holds ISO 3166-1 alpha-2 codes; render them as
      // "Name (CODE)" so the list is readable. Matching still runs server-side on the raw codes.
      if (this.rule.data?.scope !== 'country') {
        return list;
      }

      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

      return list.map((code) => {
        const upper = code.toUpperCase();
        // `.of()` throws RangeError on codes that aren't well-formed region subtags;
        // fall back to the raw code so a stray value can't crash the render.
        let name;

        try {
          name = regionNames.of(upper);
        } catch {
          return upper;
        }

        return name && name !== upper ? `${name} (${upper})` : upper;
      });
    },
    geoIpScopeSingular() {
      const scope = this.rule.data?.scope;

      return { ip: 'IP', city: 'city', country: 'country' }[scope] || scope;
    },
    // Compact, self-contained summary for the widget card — the full list of values lives
    // in the "View all" dialog and the email/Slack notifications, not on the card.
    geoIpListCardSummary() {
      const verb = this.rule.data?.match_mode === 'not_in' ? 'not matching' : 'matching';

      return `- Geo-IP ${verb} the ${this.geoIpScopeSingular} list`;
    },
    ruleSubtitle() {
      const rule = this.rule;
      const metricType = this.computedMetricType || '';

      if (['unusual_activity', 'suspicious_activity'].includes(rule.metric_type)) {
        let name;

        if (rule.metric_type === 'suspicious_activity') {
          name = 'Suspicious applications';
        } else {
          name = 'Unusual activity';
        }

        return `${metricType} - ${name}`;
      }

      // geo-IP list-match rules have no meaningful "above N" threshold (it is forced to
      // above 0); describe the configured list instead of "list match above 0 matches".
      if (this.isGeoIpListMatch) {
        return `- ${this.geoIpListIntro}`;
      }

      const condition = this.getConditionValue(rule.condition_value);
      const text = `- ${metricType} ${rule.condition} ${condition.value} ${condition.addon}`;

      return text;
    },
  },
  methods: {
    showFullDayLink(frequency) {
      return ['hourly', 'daily'].includes(frequency);
    },
    // geo-IP metrics link to the Audit Log report (filtered to the member and location
    // changes) rather than the activities/screenshots day view, mirroring the email CTA.
    auditLogUrl(userId) {
      const from = parseInternalDatetime(this.notification.from).format('YYYY-MM-DD');
      const to = parseInternalDatetime(this.notification.to).format('YYYY-MM-DD');
      // The report reads its date range from a single "from - to" string in f[date];
      // f[from]/f[to] are ignored and it falls back to the default (last 7 days).
      const params = new URLSearchParams({
        'f[member]': userId,
        'f[event_action]': 'Location changed',
        'f[date]': `${from} - ${to}`,
      });

      return `/reports/${this.organization.id}/team/audit_logs?${params.toString()}`;
    },
    trackAuditLogClick() {
      window.analytics?.track('SN view audit log');
    },
    getConditionValue(val) {
      let value, addon;
      const rule = this.rule;

      if (this.isTimeBasedMetric) {
        value = secondsToHours(val);

        if (rule.frequency === 'hourly') {
          addon = 'min/hr';
        } else if (rule.frequency === 'daily' || rule.time_unit === 'per_day') {
          addon = rule.frequency === 'daily' ? 'hr/day' : 'Avg. hr/day';
        } else if (rule.frequency === 'weekly' || rule.time_unit === 'per_week') {
          addon = 'hr/week';
        } else if (rule.frequency === 'monthly' || rule.time_unit === 'per_month') {
          addon = 'hr/month';
        } else {
          console.error(`Unexpected frequency and metric combination ${rule.frequency} ${rule.metric_type}`);
          addon = '';
        }
      } else if (this.isCountMetric) {
        // Count metrics (geo-IP changes, users sharing an IP, geo-IP list match) are not
        // percentages; show their own unit instead of "%".
        value = val;
        addon = this.countMetricAddon;
      } else {
        value = val;
        addon = '%';
      }

      return { addon, value };
    },
  },
};
